/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React, { useMemo } from 'react';

import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	ExperienceFailure,
	ExperienceStart,
	ExperienceSuccess,
} from '@atlassian/conversation-assistant-instrumentation/ui/ExperienceTracker';
import { ChatGenericError } from '@atlassian/conversation-assistant-ui-components/ui/ChatGenericError';

import { SiteContext } from '../../../controllers/site-id/index.tsx';

type Props = {
	children: React.ReactNode;
	siteId: string;
	product: string;
};

const styles = cssMap({
	wrapper: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingInline: token('space.300'),
		height: '692px',
	},
});

const ErrorFallbackComponent = ({ error }: FallbackProps) => (
	<Box xcss={styles.wrapper}>
		<ChatGenericError errorType="generic" />
		<ExperienceFailure error={error} name={ExperienceName.LOAD_BROWSE_AGENTS} />
	</Box>
);

export const BrowseAgentProviders = ({ children, siteId, product }: Props): JSX.Element => {
	const siteContext = useMemo(() => ({ siteId, product: product }), [siteId, product]);

	return (
		<>
			<ExperienceStart name={ExperienceName.LOAD_BROWSE_AGENTS} />
			<ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
				<SiteContext.Provider value={siteContext}>{children}</SiteContext.Provider>
				<ExperienceSuccess name={ExperienceName.LOAD_BROWSE_AGENTS} />
			</ErrorBoundary>
		</>
	);
};
